/**
 * Деньги в интерфейсе: подписи способов оплаты и перевод минорных единиц в человеческие.
 *
 * Множители минорных единиц берутся из `@pwz/contracts` — того же словаря, по которому считает
 * сервер: три отдельные копии `{ USDT: 100, RUB: 100, IDR: 1, THB: 1 }` в компонентах молча
 * разошлись бы с ним при первой же новой валюте. Подписи — русский текст, он принадлежит вебу,
 * но и его достаточно одного экземпляра.
 *
 * Способов рабочих четыре, но подписан здесь каждый: «Карта», «Перевод внутри страны» и
 * «Перевод в рублях» свернулись в один «Перевод», а выписка по-прежнему полна старых чеков, и
 * строка без подписи читается как поломка интерфейса, а не как история.
 */

import { MINOR_UNITS, type OriginalCurrency, type PaymentMethod } from "@pwz/contracts";
import { Banknote, Bitcoin, CreditCard, Landmark, type LucideIcon, Ticket } from "lucide-react";

export { MINOR_UNITS };

export const METHOD_LABELS: Readonly<Record<PaymentMethod, string>> = Object.freeze({
  cash: "Наличные",
  transfer: "Перевод",
  crypto: "Крипта",
  certificate: "Сертификат",
  // Старые чеки: подпись остаётся своей, чтобы выписка читалась так, как её оформили.
  local_transfer: "Перевод внутри страны",
  rub_transfer: "Перевод в рублях",
  card: "Карта",
});

/**
 * Короткие подписи для тесных мест — три чипа способа в одну строку на 390 px.
 * Живут рядом с полными: два словаря в разных файлах разъехались бы при новом способе,
 * а «Перевод внутри страны» в чипе шириной 113 px ломается на три строки.
 */
export const METHOD_SHORT_LABELS: Readonly<Record<PaymentMethod, string>> = Object.freeze({
  cash: "Наличные",
  transfer: "Перевод",
  crypto: "Крипта",
  certificate: "Сертификат",
  local_transfer: "Перевод",
  rub_transfer: "Рубли",
  card: "Карта",
});

/**
 * Значок способа. Живёт рядом с подписями: способ рисуют и шторка сделки, и очередь платежей
 * админки, и две копии одной таблицы разошлись бы при первом же новом способе. В плотной
 * выписке значок читается раньше текста — строку находят по силуэту, а не перебором.
 */
export const METHOD_ICONS: Readonly<Record<PaymentMethod, LucideIcon>> = Object.freeze({
  cash: Banknote,
  transfer: Landmark,
  crypto: Bitcoin,
  certificate: Ticket,
  local_transfer: Landmark,
  rub_transfer: Landmark,
  card: CreditCard,
});

/**
 * Способы, у которых обязан быть чек.
 *
 * Наличные подтверждаются передачей денег из рук в руки, сертификат — самим бланком. Остальное
 * приходит переводом, и у перевода всегда есть скриншот: его просим приложить и показываем в
 * операции. Отправку это не блокирует — amo Drive может лежать, и чек догружают позже, — но
 * перевод без чека виден как таковой, и понятно, у кого его спрашивать.
 */
export const PROOF_EXPECTED_METHODS: ReadonlySet<PaymentMethod> = new Set<PaymentMethod>([
  "transfer",
  "crypto",
  "card",
  "local_transfer",
  "rub_transfer",
]);

/** Сумма в единицах валюты из суммы в минорных: 100 500 копеек → 1005 ₽. */
export function fromMinor(amountMinor: number, currency: OriginalCurrency): number {
  return amountMinor / MINOR_UNITS[currency];
}

/** Сумма в минорных единицах из введённой человеком: 1005 ₽ → 100 500 копеек. */
export function toMinor(amount: number, currency: OriginalCurrency): number {
  return Math.round(amount * MINOR_UNITS[currency]);
}
